import { Carousel, CarouselItem, HeaderItemStyle } from '@almbrand/carousel';
import { Headline } from '@almbrand/headline';
import { useDeviceQuery } from '@almbrand/usedevicequery';
import { CalculationOptions, ModalContentComponent } from 'components/1-atoms';
import { CtaGroupComponent } from 'components/2-molecules/CtaGroupComponent/CtaGroupComponent';
import { PageContext } from 'context/PageContext';
import { useModal, usePageParam } from 'hooks';
import { useContext, useEffect, useRef, useState } from 'react';
import { FieldValues, UseFormRegister, UseFormTrigger } from 'react-hook-form';
import { useAppSelector } from 'store/hooks';
import { calculatePackage } from '../../../api/CalculateApi';
import { mapPackages } from '../../../services/PackageService';
import styles from './PackageComponent.module.scss';
import { PackageTile } from './tiles';

interface Prices {
	priceBase?: number;
	priceDiscount?: number;
	hasError?: boolean;
}

interface PackageComponentProps {
	index: number;
	register: UseFormRegister<FieldValues>;
	contentType: string;
	pageId: string;
	submitStep?: () => void;
	hasError?: boolean;
	trigger?: UseFormTrigger<FieldValues>;
	stepTitle?: string;
	errorMessage?: string;
	fields: Fields;
}

export const PackageComponent: React.FC<PackageComponentProps> = ({ fields, pageId, ...props }) => {
	const packageRef = useRef<HTMLDivElement>();
	const { getPageParam } = usePageParam();

	const packages: Map<string, PackageConfig> = mapPackages(fields);

	const pageContext = useContext(PageContext);

	const [priceFormFields, setPriceFormFields] = useState<Array<string>>();

	const [ipidField, setIpidField] = useState<Modal>(undefined);

	const [calculationOptions, setCalculationOptions] = useState<CalculationOptionsBlock[]>(undefined);

	const [selectionError, setSelectionError] = useState<boolean>(undefined);

	const form = useAppSelector((state) => state.calculatorForm);

	const [calcParams, setCalcParams] = useState<Map<string, string>>();

	const [doCalculate, setDoCalculate] = useState<boolean>(false);

	const [doSetupCalculation, setDoSetupCalculation] = useState<number>(0);

	const [allCalculationsDone, setAllCalculationsDone] = useState<boolean>(false);

	const [callOrder, setCallOrder] = useState<Array<number>>();

	const [packageNames, setPackageNames] = useState<Array<string>>();
	const [packagePrices, setPackagePrices] = useState<Map<string, Prices>>();
	const [activeItem, setActiveItem] = useState<number>();

	const { displayErrorModal } = useModal();

	const [isValid, setIsValid] = useState(false);

	const { isMobile } = useDeviceQuery();

	// mandatory navigation buttons from CMS
	const ctaGroupBlock = fields.items.find((field) => field.content.contentType === 'CtaGroupBlock');

	const addPackagePrice = (map: Map<string, Prices>) => {
		const mergedMap = new Map([...Array.from(packagePrices.entries()), ...Array.from(map.entries())]);
		setPackagePrices(mergedMap);
	};

	const getPrice = async (calcParams: Map<string, string>, packageName: string) => {
		if (!doCalculate) {
			return;
		}
		try {
			// DEBUG throw new Error('test error');
			const res: any = await calculatePackage(
				calcParams,
				pageContext.metaArea,
				packages.get(packageName).coverages
			);
			return res.total_price / 12;
		} catch (e) {
			console.error(e);
			let title: string;
			let subtitle: string;
			let content: string;

			const detail: string = e?.response?.data?.detail;
			if (detail?.includes('SPD - Message : {')) {
				const spdMsg = detail.substring(detail.indexOf('SPD - Message : {') + 16, detail.length - 1);
				const spdError = JSON.parse(spdMsg);
				const { errors } = spdError;
				if (errors?.length > 0) {
					title = errors[0].errorCode;
					subtitle = 'Error message';
					content = errors[0].errorMessage;
				}
			} else if (e?.code === 'ECONNABORTED') {
				title = 'Request timeout';
				subtitle = 'Error message';
				content = e.message;
			}

			displayErrorModal(title, subtitle, content);
			const ref = location.href.toLowerCase();
			// possible to mock calculations on test environments -> add query param "mock=1"
			if (ref.includes('mock=') && (ref.startsWith('https://localhost') || ref.startsWith('https://sit-'))) {
				// simulate delay
				const delay = Math.floor(Math.random() * 5) * 1000;
				await new Promise((res) => setTimeout(res, delay + 1000));
				// get price in test
				return Number.parseInt(500 * Math.random() + '');
			}
			return undefined;
		}
	};

	const getCallOrder = (inx: number, callOrderCopy: number[]): number[] => {
		const order = [...callOrderCopy];
		order.sort((a, b) => a - b);
		order.unshift(order.splice(inx, 1)[0]);
		return order;
	};

	const mapComponents = (): CarouselItem[] => {
		const headerItemStyle: HeaderItemStyle = {
			color: 'secondaryGrey',
			colorActive: 'primaryDark',
			background: undefined,
			backgroundActive: 'primaryWhite',
		};
		return packageNames.map((packageName, index) => {
			const prices = packagePrices.get(packageName);
			const packageConfig = packages.get(packageName);
			const carouselItem: CarouselItem = {
				header: packageName,
				headerItemStyle,
				jsxElementBackgroundColor: 'secondaryLightGrey',

				jsxElement: (
					<PackageTile
						header={{
							title: packageName,
							discountText: packageConfig.discountText,
						}}
						priceBase={prices?.priceBase}
						priceDiscount={prices?.priceDiscount}
						hasCalculationError={prices?.hasError}
						noSelectionError={selectionError && !prices?.hasError}
						errorMessage={props.errorMessage}
						coverages={packageConfig.coverages}
						isLoading={prices?.priceBase === undefined}
						allCalculationsDone={allCalculationsDone}
						getIsValid={(value) => setIsValid(value)}
						setActiveItem={setActiveItem}
						index={index}
					/>
				),
			};
			return carouselItem;
		});
	};
	// setting up which input fields to use for calculations - for now only on create component
	useEffect(() => {
		const priceFormFieldsInit = [];

		for (const key of Object.keys(form)) {
			priceFormFieldsInit.push(key);
		}
		setPriceFormFields(priceFormFieldsInit);
	}, []);

	useEffect(() => {
		if (priceFormFields && pageId === getPageParam()) {
			setDoCalculate(false);
			setDoSetupCalculation((prev) => prev + 1);
			setupIpidAndCalculationOptions();
		}
	}, [getPageParam(), priceFormFields]);

	const setupIpidAndCalculationOptions = () => {
		if (!ipidField) {
			for (const field of fields?.items ?? []) {
				if (field?.content?.contentType === 'ModalContentBlock') {
					setIpidField(field.content);
				}
			}
		}
		if (!calculationOptions) {
			const calculationOptions = pageContext.metaArea.items[0].content.calculationOptions;

			const calcOptions: CalculationOptionsBlock[] = [];
			for (const calculationOption of calculationOptions?.items ?? []) {
				//only push selections that the user can choose from
				if (calculationOption.content?.contentType === 'CalculationOptionsBlock') {
					calcOptions.push(calculationOption.content);
				}
			}
			setCalculationOptions(calcOptions.length > 0 ? calcOptions : undefined);
		}
	};

	useEffect(() => {
		if (!priceFormFields) {
			return;
		}

		setAllCalculationsDone(false);
		const map: Map<string, string> = new Map();

		const calculatorParameterPrefix = pageContext.metaArea?.items[0]?.content?.calculatorParameterPrefix;
		for (const key of priceFormFields) {
			if (form[key].data && key?.includes(calculatorParameterPrefix)) {
				map.set(form[key].data.id, form[key].data.value);
			}
		}

		setCalcParams(map);

		const initPackagePricesMap = new Map<string, Prices>();
		const initPackageNames: string[] = [];
		const order: number[] = [];
		let count = 0;
		let primary = 0;
		for (const [key, packageConfig] of Array.from(packages.entries())) {
			initPackagePricesMap.set(key, { priceBase: undefined, priceDiscount: undefined, hasError: false });
			initPackageNames.push(key);
			order.push(count);
			if (packageConfig.isPrimaryPackage) {
				primary = count;
			}
			count++;
		}

		setPackagePrices(initPackagePricesMap);
		setPackageNames(initPackageNames);

		if (!callOrder) {
			setCallOrder(getCallOrder(primary, order));
		}

		setDoCalculate(true);
	}, [doSetupCalculation]);

	useEffect(() => {
		if (!doCalculate) {
			return;
		}
		// make certain that calcOrder is not affected by user changes during calculations
		const packageOrder = [...callOrder];

		const resMap = new Map<string, Prices>();
		for (const calcInx of packageOrder) {
			// call all calculation without wait -> then(...)
			getPrice(calcParams, packageNames[calcInx]).then((price) => {
				resMap.set(packageNames[calcInx], {
					priceBase: price,
					priceDiscount: price * 0.9,
					hasError: !price || price < 1,
				});
				addPackagePrice(resMap);
			});
		}
	}, [doCalculate]);

	// checking if all calculations have been run and setting states accordingly
	useEffect(() => {
		let finishedCalculations = true;
		if (packagePrices) {
			for (const price of Array.from(packagePrices.values())) {
				if (finishedCalculations) {
					finishedCalculations = price.priceBase !== undefined;
				}
			}
			if (finishedCalculations) {
				setDoCalculate(false);
				setAllCalculationsDone(true);
			}
		}
	}, [packagePrices]);

	useEffect(() => {
		if (activeItem >= 0 && callOrder?.length > 0) {
			setCallOrder(getCallOrder(activeItem, callOrder));
		}
	}, [activeItem]);

	useEffect(() => {
		if (isValid) {
			setSelectionError(!isValid);
		}
	}, [isValid]);

	return (
		callOrder && (
			<div
				className={styles.packages}
				ref={packageRef}
			>
				<Carousel
					topSection={
						<div className={styles.packages__topSection}>
							<Headline
								title={props.stepTitle}
								variant='h2'
								fontColor='white'
								size='default'
							/>
						</div>
					}
					headerItemsBackgroundColor={'#081047'}
					items={mapComponents()}
					dragThreshold={35}
					getActiveItem={setActiveItem}
					// Starting offset on first render. So to get the correct UI if you go from desktop to mobile you should refresh the page.
					offset={isMobile ? 'left' : 'center'}
					springEffect={{ stiffness: 200, damping: 20 }}
					// Starting tile on first render. So to get the correct UI if you go from desktop to mobile you should refresh the page.
					startingItem={isMobile ? callOrder[0] : 0}
				/>

				{calculationOptions && (
					<div className={styles.packages__bottomSection}>
						<CalculationOptions
							buttons={calculationOptions}
							allCalculationsDone={allCalculationsDone}
							calculationOptionChanged={() => {
								setDoSetupCalculation((prev) => prev + 1);
							}}
							{...props}
						/>
					</div>
				)}
				{ipidField && (
					<div className={styles.packages__bottomSection}>
						<ModalContentComponent {...ipidField} />
					</div>
				)}
				<CtaGroupComponent
					ctaGroup={(ctaGroupBlock.content as any).ctaGroup}
					pageId={pageId}
					isValid={isValid}
					isPackageStep={true}
					beforeSubmit={() => setSelectionError(!isValid)}
				/>
			</div>
		)
	);
};
